import { Component, computed, inject, input, output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { combineLatest, distinctUntilChanged, map, switchMap } from 'rxjs';

import { LanguageService } from '@fcom/ui-translate/services/language.service';
import { MediaQueryService } from '@fcom/common/services/media-query/media-query.service';
import { PageMetaService } from '@fcom/common/services/page-meta/page-meta.service';
import { TripType } from '@fcom/core/interfaces/triptype.interface';
import { CmsTaxonomy, CmsTaxonomyGeneral } from '@fcom/core-api/interfaces/cms-interface';
import { GlobalBookingTravelClass } from '@fcom/core/interfaces/global-booking-travel-class.interface';
import { LocationRouteCffService } from '@fcom/core/services/location-routecff/location-routecff.service';
import { finShare } from '@fcom/rx/operators/finShare';
import { FetchPriorityEnum } from '@fcom/ui-components/components/images/enums';

import { BlockOffer, BlockOfferData } from '../../interfaces/destination-promo.interface';
import { getHeaderLevel } from '../../services/cms-utils/cms-utils';

@Component({
  selector: 'fin-offer-grid',
  styleUrls: ['./offer-grid.component.scss'],
  templateUrl: './offer-grid.component.html',
  standalone: false,
})
export class OfferGridComponent {
  private languageService = inject(LanguageService);
  private locationRouteCffService = inject(LocationRouteCffService);
  private pageMetaService = inject(PageMetaService);
  private mediaQueryService = inject(MediaQueryService);

  destinationClicked = output<[BlockOffer, number]>();
  scrolledPastDestination = output<{ offer: BlockOfferData; index: number }>();
  calendarClicked = output<[Event, BlockOffer, GlobalBookingTravelClass, TripType, number]>();
  flightHotelClicked = output<{
    amLink: string;
    destinationTitle: string;
    destinationCode: string;
    offer: BlockOfferData;
    index: number;
  }>();

  offers = input<BlockOfferData[]>();
  tripType = input<TripType>();
  title = input<string>();
  cmsTags = input<CmsTaxonomy[]>();
  mobileCarousel = input(false);
  noHeaderPadding = input(false);
  fetchImagePriority = input<FetchPriorityEnum>();

  public readonly TAXONOMY: typeof CmsTaxonomyGeneral = CmsTaxonomyGeneral;

  readonly isMobile = this.mediaQueryService.isBreakpoint('mobile');

  readonly cmsStylesOverride = {
    'margin-top': '0.3rem',
    'margin-bottom': '0.9rem',
  };

  readonly headerLevel = computed<number | undefined>(() => {
    return getHeaderLevel(this.cmsTags());
  });

  private readonly origin$ = this.pageMetaService.defaultOriginLocationCode$.pipe(
    switchMap((origin) => {
      return this.locationRouteCffService.bestGuessFor(origin, this.languageService.localeValue);
    }),
    distinctUntilChanged(),
    finShare()
  );

  readonly selectedOriginLocationName = toSignal(
    combineLatest([this.pageMetaService.defaultOriginLocationCode$, this.origin$]).pipe(
      map(([originLocationCode, originLocation]) => {
        return originLocation.cityName ?? originLocationCode;
      })
    )
  );

  destinationClick([destinationItem, _travelClass]: [BlockOffer, GlobalBookingTravelClass], index: number): void {
    this.destinationClicked.emit([destinationItem, index]);
  }

  emitFlightHotelClick(
    event: { amLink: string; destinationTitle: string; destinationCode: string },
    offer: BlockOfferData,
    index: number
  ): void {
    this.flightHotelClicked.emit({
      ...event,
      offer,
      index,
    });
  }

  emitCalendarClick(
    [event, destinationItem, travelClass, tripType]: [Event, BlockOffer, GlobalBookingTravelClass, TripType],
    index: number
  ): void {
    this.calendarClicked.emit([event, destinationItem, travelClass, tripType, index]);
  }
}
